let print: string | null = null;

export async function fingerprint(): Promise<string> {
  if (print !== null) {
    return Promise.resolve(print);
  }

  const components = {
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
    },
    timezone: {
      base: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: new Date().getTimezoneOffset(),
    },
    navigator: {
      userAgent: window.navigator.userAgent,
    },
    languages: window.navigator.languages,
    hardware: {
      concurrency: navigator.hardwareConcurrency,
    },
    platform: navigator.platform,
    vendor: navigator.vendor,
    canvas: getAdvancedCanvasFingerprint(),
    webgl: getAdvancedWebGLFingerprint(),
  };

  print = await hashFingerprint(components);

  return print;
}

async function hashFingerprint(components: Record<string, unknown>): Promise<string> {
  const jsonString = JSON.stringify(components, Object.keys(components).sort());
  const encoder = new TextEncoder();
  const data = encoder.encode(jsonString);

  if (crypto.subtle) {
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  }

  // Fallback for insecure contexts (HTTP) where crypto.subtle is unavailable
  let hash = 0x811c9dc5;
  for (const byte of data) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(16).padStart(8, "0");
}

function getAdvancedCanvasFingerprint(): string {
  const canvas = document.createElement("canvas");
  canvas.width = 280;
  canvas.height = 60;
  const ctx = canvas.getContext("2d");

  if (ctx === null) {
    return "";
  }

  // Test text rendering with multiple fonts
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#f60";
  ctx.fillRect(125, 1, 62, 20);

  // Gradient test
  const gradient = ctx.createLinearGradient(0, 0, 150, 0);
  gradient.addColorStop(0, "red");
  gradient.addColorStop(1, "blue");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 150, 20);

  // Test various fonts (rendering differs by system)
  ctx.fillStyle = "#069";
  ctx.font = "11pt Arial";
  ctx.fillText("Cwm fjordbank glyphs vext quiz 😃", 2, 15);

  ctx.font = "18pt Arial";
  ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
  ctx.fillText("Cwm fjordbank glyphs vext quiz 😃", 4, 45);

  // Test emoji rendering (very OS-specific)
  ctx.font = "14pt Segoe UI Emoji";
  ctx.fillText("🎨🎭🎪🎢", 100, 30);

  // Global composite operations test
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = "rgb(255,0,255)";
  ctx.beginPath();
  ctx.arc(50, 50, 50, 0, Math.PI * 2, true);
  ctx.closePath();
  ctx.fill();

  return canvas.toDataURL();
}

function getAdvancedWebGLFingerprint(): Record<string, unknown> | null {
  const canvas = document.createElement("canvas");
  const gl = (canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;

  if (!gl) return null;

  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info") as { UNMASKED_VENDOR_WEBGL: number; UNMASKED_RENDERER_WEBGL: number } | null;

  // Collect extensive WebGL parameters
  const fingerprint: Record<string, unknown> = {
    // Hardware info
    vendor: gl.getParameter(debugInfo?.UNMASKED_VENDOR_WEBGL ?? gl.VENDOR) as unknown,
    renderer: gl.getParameter(debugInfo?.UNMASKED_RENDERER_WEBGL ?? gl.RENDERER) as unknown,

    // Capabilities
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as unknown,
    maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) as unknown,
    maxVertexTextureImageUnits: gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS) as unknown,
    maxCombinedTextureImageUnits: gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS) as unknown,
    maxVertexAttribs: gl.getParameter(gl.MAX_VERTEX_ATTRIBS) as unknown,
    maxVaryingVectors: gl.getParameter(gl.MAX_VARYING_VECTORS) as unknown,
    maxFragmentUniformVectors: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS) as unknown,
    maxVertexUniformVectors: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS) as unknown,
    aliasedLineWidthRange: gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE) as unknown,
    aliasedPointSizeRange: gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as unknown,
    maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS) as unknown,
    maxAnisotropy: getMaxAnisotropy(gl),

    // Supported extensions (very device-specific)
    extensions: gl.getSupportedExtensions(),

    // Shader precision (varies by GPU)
    shaderPrecision: {
      vertexHighFloat: getPrecision(gl, gl.VERTEX_SHADER, gl.HIGH_FLOAT),
      vertexMediumFloat: getPrecision(gl, gl.VERTEX_SHADER, gl.MEDIUM_FLOAT),
      fragmentHighFloat: getPrecision(gl, gl.FRAGMENT_SHADER, gl.HIGH_FLOAT),
      fragmentMediumFloat: getPrecision(gl, gl.FRAGMENT_SHADER, gl.MEDIUM_FLOAT),
    },
  };

  return fingerprint;
}

function getMaxAnisotropy(gl: WebGLRenderingContext): unknown {
  const ext = (gl.getExtension("EXT_texture_filter_anisotropic")
    ?? gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic")
    ?? gl.getExtension("MOZ_EXT_texture_filter_anisotropic")) as { MAX_TEXTURE_MAX_ANISOTROPY_EXT: number } | null;

  return ext ? gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as unknown : null;
}

function getPrecision(gl: WebGLRenderingContext, shaderType: number, precisionType: number): Record<string, number> {
  const precision = gl.getShaderPrecisionFormat(shaderType, precisionType);
  return {
    rangeMin: precision?.rangeMin ?? 0,
    rangeMax: precision?.rangeMax ?? 0,
    precision: precision?.precision ?? 0,
  };
}
